/// <amd-module name="VSSUI/Components/TextField/TextField" />
import * as React from "react";

import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { FocusZoneContext, IFocusZoneContext } from "VSSUI/FocusZone";
import { FormItemContext, IFormItemContext, Severity } from "VSSUI/FormItem";
import { Icon, IconSize } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeId, KeyCode } from "VSSUI/Util";
import { getTabIndex } from "VSSUI/Utilities/Focus";
import { ITextField, ITextFieldProps, TextFieldFocusTreatmentBehavior, TextFieldStyle, TextFieldWidth } from "./TextField.Props";

let inputId = 1;

export interface ITextFieldState {
    textLength: number;
}

export class TextField extends React.Component<ITextFieldProps, ITextFieldState> implements ITextField {
    private inputId: string;
    public inputElement: React.RefObject<HTMLInputElement & HTMLTextAreaElement>;

    constructor(props: ITextFieldProps) {
        super(props);

        this.inputId = `textfield-input-${inputId++}`;
        this.inputElement = props.inputElement || React.createRef();
        this.state = { textLength: props.showCharacterCounter ? this.getTextLength(props.value) : 0 };
    }

    public focus() {
        if (this.inputElement.current) {
            this.inputElement.current.focus();
        }
    }

    public readonly select = () => {
        if (this.inputElement.current) {
            this.inputElement.current.select();
        }
    };

    public get selectionEnd(): number | null {
        return this.inputElement.current ? this.inputElement.current.selectionEnd : null;
    }

    public get selectionStart(): number | null {
        return this.inputElement.current ? this.inputElement.current.selectionStart : null;
    }

    public setSelectionRange(start: number, end: number, direction?: "forward" | "backward" | "none" | undefined) {
        if (this.inputElement.current) {
            this.inputElement.current.setSelectionRange(start, end, direction);
        }
    }

    public render() {
        const {
            autoAdjustHeight,
            className,
            containerClassName,
            disabled,
            focusTreatment = TextFieldFocusTreatmentBehavior.all,
            inputId,
            label,
            onBlur,
            onFocus,
            onChange,
            required,
            style,
            value,
            width
        } = this.props;

        const localOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string) => {
            onChange && onChange(event, newValue);
            if (this.props.maxLength && this.props.showCharacterCounter) {
                this.setState({ textLength: newValue.length });
            }
        };

        const input = (
            <FocusWithin onFocus={onFocus} onBlur={onBlur}>
                {(focusStatus: IFocusWithinStatus) => (
                    <FormItemContext.Consumer>
                        {formItemContext => {
                            return (
                                <div className={css("flex-column", containerClassName, width !== TextFieldWidth.auto && width)}>
                                    <div
                                        className={css(
                                            !label && className,
                                            "bolt-textfield flex-row flex-center",
                                            disabled && "disabled",
                                            focusTreatment === TextFieldFocusTreatmentBehavior.all && "focus-treatment",
                                            focusTreatment === TextFieldFocusTreatmentBehavior.keyboardOnly && "focus-keyboard-only",
                                            focusStatus.hasFocus && "focused",
                                            style === TextFieldStyle.inline && "bolt-textfield-inline",
                                            formItemContext.error && (!formItemContext.severity || formItemContext.severity === Severity.Error) && "bolt-textfield-error",
                                            formItemContext.error && formItemContext.severity === Severity.Warning && "bolt-textfield-warning"
                                        )}
                                    >
                                        <FocusZoneContext.Consumer>
                                            {zoneContext => (
                                                <Observer value={value}>
                                                    {(observedProps: { value?: string }) => {
                                                        return (
                                                            <TextFieldInnerValue
                                                                {...this.props}
                                                                onKeyDown={event => {
                                                                    if (this.props.multiline && event.keyCode === KeyCode.enter && !event.ctrlKey) {
                                                                        event.stopPropagation();
                                                                        return;
                                                                    }

                                                                    this.props.onKeyDown?.(event);
                                                                }}
                                                                onChange={localOnChange}
                                                                focus={() => this.focus()}
                                                                required={required}
                                                                focusStatus={focusStatus}
                                                                formItemContext={formItemContext}
                                                                inputElement={this.inputElement}
                                                                inputId={this.props.inputId || this.inputId}
                                                                value={observedProps.value}
                                                                zoneContext={zoneContext}
                                                            />
                                                        );
                                                    }}
                                                </Observer>
                                            )}
                                        </FocusZoneContext.Consumer>
                                    </div>
                                    {this.props.maxLength && this.props.showCharacterCounter && (
                                        <div className="bolt-textfield-counter">
                                            <span>
                                                {this.state.textLength}/{this.props.maxLength}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        }}
                    </FormItemContext.Consumer>
                )}
            </FocusWithin>
        );

        if (label) {
            return (
                <div className={css(className, "flex-column")}>
                    <label htmlFor={getSafeId(inputId || this.inputId)} className={required ? "bolt-textfield-label bolt-textfield-label--required" : "bolt-textfield-label"}>
                        {label}
                    </label>
                    {input}
                </div>
            );
        } else {
            return input;
        }
    }

    private getTextLength = (value?: IReadonlyObservableValue<string | undefined> | string): number => {
        if (!value) {
            return 0;
        } else if (typeof value === "string") {
            return (value as string).length;
        } else {
            return (value as IReadonlyObservableValue<string | undefined>).value?.length || 0;
        }
    };
}

interface ITextFieldInnerValueProps extends ITextFieldProps {
    focus: () => void;
    focusStatus: IFocusWithinStatus;
    formItemContext: IFormItemContext;
    inputElement: React.RefObject<HTMLInputElement & HTMLTextAreaElement>;
    inputId: string;
    value?: string;
    zoneContext: IFocusZoneContext;
}

class TextFieldInnerValue extends React.Component<ITextFieldInnerValueProps> {
    private adjustedHeight = -1;
    private adjustedHeightValue: string = "";
    private hiddenElement = React.createRef<HTMLInputElement & HTMLTextAreaElement>();

    public render() {
        const {
            activatable,
            ariaActiveDescendant,
            ariaAutoComplete,
            ariaControls,
            ariaExpanded,
            ariaHasPopup,
            ariaInvalid,
            ariaLabel,
            ariaLabelledBy,
            ariaRoleDescription,
            autoAdjustHeight,
            autoComplete,
            autoFocus,
            autoSelect,
            disabled,
            excludeFocusZone,
            focus,
            focusStatus,
            formItemContext,
            inputClassName,
            inputElement,
            inputId,
            inputType,
            maxLength,
            maxWidth,
            multiline,
            onClick,
            onKeyDown,
            onKeyPress,
            onKeyUp,
            onPaste,
            placeholder,
            prefixIconProps,
            readOnly,
            required,
            resizable,
            role,
            rows,
            spellCheck,
            tooltipProps,
            value,
            zoneContext
        } = this.props;

        let { ariaDescribedBy, suffixIconProps } = this.props;

        const TagName = multiline ? "textarea" : "input";
        const tagSpecificProps = multiline ? { rows: rows } : { type: inputType, autoComplete: autoComplete ? "on" : "off" };

        if (suffixIconProps === undefined && formItemContext.error) {
            if (formItemContext.severity === Severity.Warning) {
                suffixIconProps = {
                    className: "bolt-textfield-message-warning",
                    iconName: "Warning"
                };
            } else {
                suffixIconProps = {
                    className: "bolt-textfield-message-error",
                    iconName: "Error"
                };
            }
        }

        if (ariaDescribedBy === undefined) {
            ariaDescribedBy = formItemContext.ariaDescribedById;
        }

        const className = css(
            inputClassName,
            "bolt-textfield-input flex-grow",
            autoAdjustHeight && "bolt-textfield-auto-adjust",
            resizable && "bolt-textfield-auto-unresizable",
            prefixIconProps && "bolt-textfield-input-with-prefix",
            suffixIconProps && "bolt-textfield-input-with-suffix",
            activatable && "activatable"
        );

        const style = maxWidth !== undefined ? { maxWidth: maxWidth } : {};

        let element = (
            <TagName
                {...tagSpecificProps}
                aria-activedescendant={getSafeId(ariaActiveDescendant)}
                aria-autocomplete={ariaAutoComplete}
                aria-controls={getSafeId(ariaControls)}
                aria-describedby={getSafeId(ariaDescribedBy)}
                aria-disabled={disabled}
                aria-expanded={ariaExpanded}
                aria-haspopup={ariaHasPopup}
                aria-invalid={ariaInvalid}
                aria-label={ariaLabel === undefined && placeholder ? placeholder : ariaLabel}
                aria-labelledby={getSafeId(ariaLabelledBy) || getSafeId(formItemContext.ariaLabelledById)}
                aria-readonly={inputType && inputType !== "text" ? readOnly : undefined}
                aria-roledescription={ariaRoleDescription}
                autoFocus={autoFocus}
                data-focuszone={!disabled && !excludeFocusZone ? zoneContext.focuszoneId : undefined}
                disabled={disabled}
                className={className}
                id={getSafeId(inputId)}
                maxLength={maxLength}
                onBlur={focusStatus.onBlur}
                onClick={onClick}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    this.props.onChange && this.props.onChange(e, e.target.value);

                    // Adjust height synchronously. If we wait until the React update effect, then
                    // the text area has already been painted in a partially-scrolled state which
                    // causes some ugly flickering.
                    this.adjustHeight();
                }}
                onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    if (autoSelect && inputElement.current) {
                        inputElement.current.select();
                    }
                    focusStatus.onFocus && focusStatus.onFocus(event);
                }}
                onKeyDown={onKeyDown}
                onKeyPress={onKeyPress}
                onKeyUp={onKeyUp}
                onPaste={onPaste}
                placeholder={placeholder}
                readOnly={readOnly}
                required={required}
                ref={inputElement}
                role={role}
                style={style}
                spellCheck={spellCheck}
                tabIndex={getTabIndex(this.props)}
                value={value || ""}
            />
        );

        // We will use a hidden element behind the input to measure the height.
        // This prevents the page from performing re-layout when measuring.
        if (multiline && autoAdjustHeight) {
            element = (
                <div className="flex-row flex-grow relative">
                    <TagName
                        aria-hidden={true}
                        className={css("bolt-textfield-auto-adjust-hidden", className)}
                        ref={this.hiddenElement}
                        role="presentation"
                    />
                    {element}
                </div>
            );
        }

        return (
            <>
                {prefixIconProps &&
                    Icon({
                        size: IconSize.medium,
                        ...prefixIconProps,
                        className: css(
                            prefixIconProps.className,
                            "prefix",
                            !prefixIconProps.render && "bolt-textfield-icon",
                            ((placeholder && !value) || prefixIconProps.render) && "bolt-textfield-no-text"
                        ),
                        onClick: e => {
                            prefixIconProps && prefixIconProps.onClick && prefixIconProps.onClick(e);
                            focus();
                        }
                    })}
                {tooltipProps ? <Tooltip {...tooltipProps}>{element}</Tooltip> : element}
                {suffixIconProps &&
                    Icon({
                        size: IconSize.medium,
                        ...suffixIconProps,
                        className: css(
                            suffixIconProps.className,
                            "suffix",
                            !suffixIconProps.render && "bolt-textfield-icon",
                            ((placeholder && !value) || suffixIconProps.render) && "bolt-textfield-no-text",
                            suffixIconProps.onClick && "cursor-pointer"
                        ),
                        onClick: e => {
                            suffixIconProps && suffixIconProps.onClick && suffixIconProps.onClick(e);
                            focus();
                        }
                    })}
            </>
        );
    }

    public componentDidMount() {
        this.adjustHeight();
    }

    public componentDidUpdate() {
        this.adjustHeight();
    }

    private adjustHeight() {
        const { hiddenElement } = this;
        const { autoAdjustHeight, inputElement, multiline, value } = this.props;

        if (!autoAdjustHeight) {
            this.adjustedHeightValue = "";
            this.adjustedHeight = -1;
            return;
        }

        // If this is a multi-line, auto-adjust text area, adjust the height based on the current content
        if (multiline && inputElement.current && hiddenElement.current && value !== this.adjustedHeightValue) {
            hiddenElement.current.value = inputElement.current.value;

            if (this.adjustedHeight !== hiddenElement.current.scrollHeight) {
                this.adjustedHeight = hiddenElement.current.scrollHeight;
                this.adjustedHeightValue = inputElement.current.value;

                inputElement.current.style.height = this.adjustedHeight + "px";
            }
        }
    }
}
